/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { EllipsisVertical } from 'lucide-react';

import type { ModelRunsQueryResult } from '@/api/generated';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import useDownload from '@/hooks/use-download';
import { useFileDownload } from '@/hooks/use-file-download';

type ModelRun = ModelRunsQueryResult['data'][number];

export const columns: Array<ColumnDef<ModelRun>> = [
  {
    id: 'actions',
    accessorFn: (row) => row.id,
    header: () => null,
    cell: ({ row }) => {
      const { t } = useTranslation();
      const { downloadFile: downloadInput, isPending: isDownloadingInput } =
        useFileDownload({
          mutationKey: row.original.id,
          path: `/api/files/${row.original.input.id}/download`,
        });
      const { downloadFile: downloadOutput, isPending: isDownloadingOutput } =
        useFileDownload({
          mutationKey: row.original.id,
          path: `/api/files/${row.original.output?.id}/download`,
        });
      const { download } = useDownload();

      const handleDownloadInput = useCallback(async () => {
        const { url, fileName } = await downloadInput();
        download({
          url,
          file: fileName,
        });
      }, [download, downloadInput]);

      const handleDownloadOutput = useCallback(async () => {
        const { url, fileName } = await downloadOutput();
        download({
          url,
          file: fileName,
        });
      }, [download, downloadOutput]);

      return (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant={'outline'}>
              <Button.Icon icon>
                <EllipsisVertical />
              </Button.Icon>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={handleDownloadInput}>
              {isDownloadingInput ? (
                <Spinner size={'small'} />
              ) : (
                t('clusters.table.downloadInput')
              )}
            </DropdownMenuItem>
            {row.original.output?.id && (
              <DropdownMenuItem onClick={handleDownloadOutput}>
                {isDownloadingOutput ? (
                  <Spinner size={'small'} />
                ) : (
                  t('clusters.table.downloadOutput')
                )}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
